import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { tm } from '@captaincause/ui/tm'
import qs from 'qs'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { useMutation } from '@captaincause/lib/api/useMutation'
import { Badge } from '@captaincause/ui/Badge'
import {
  getImpactSector,
  getTheme,
  getThemeLabel,
} from '@captaincause/lib/project'
import { Button } from 'components/Button'
import { SortingMenu } from './SortingMenu'
import { SearchBar } from './Searchbar'
import { FilterPanel } from './FilterPanel'
import type { Ref } from 'react'

import type {
  ImpactSector,
  Locales,
  ProjectFilters,
  ProjectTheme,
  ProjectThemeWithLabel,
  Project as ProjectType,
  StringHeaders,
  Theme,
} from '@captaincause/types'

interface ProjectFiltersBarProps {
  activatedSortingClassName?: string
  className?: string
  projects: ProjectType[]
  filters: ProjectFilters
  catalogId?: string
  userId?: string
  onFilter: (projects: ProjectType[]) => void
  withSorting?: boolean
  searchHTTPHeaders?: StringHeaders
  defaultThemeFilters?: Theme[]
}

export interface ProjectFiltersBarImperativeRef {
  clearFilters: () => void
}

export type SortingFilter =
  | 'no_sorting'
  | 'by_amount_asc'
  | 'by_contributor_asc'
  | 'by_amount_desc'
  | 'by_contributor_desc'

export type State = {
  region: string[]
  ecology: Theme[]
  social: Theme[]
  sorting: SortingFilter
}

type ActionType =
  | 'ALL'
  | 'ALL_ECOLOGY'
  | 'ALL_SOCIAL'
  | 'ONLY_ONE_TAG'
  | 'TOGGLE_IMPACT_SECTOR'
  | 'TOGGLE_FILTER_TAG'
  | 'SORTING_TAG'
  | 'RESET'
  | 'REPLACE_STATE'

export type SortingTag = Theme | string | SortingFilter

export type Action = {
  type: ActionType
  tag?: SortingTag
  state?: State
  category?: 'region' | 'ecology' | 'social' | 'sorting'
}

function extractThemesByImpactSector(
  themes: ProjectTheme[],
  impactSector: ImpactSector,
  locale: Locales,
) {
  return themes
    .reduce((acc: ProjectThemeWithLabel[], current) => {
      const theme = current ? getTheme(current, locale) : undefined
      if (!theme || theme.impact_sector !== impactSector) return acc

      return [...acc, theme]
    }, [])
    .sort((a, b) => (a.label > b.label ? 1 : -1))
}

const defaultState: State = {
  ecology: [],
  social: [],
  region: [],
  sorting: 'no_sorting',
}

function toggle<T>({ list, item }: { list: T[]; item: T }): T[] {
  return list.some(value => item === value)
    ? list.filter(value => item !== value)
    : [...list, item]
}

const empty: Theme[] = []
export function ProjectFiltersBarWithRef(
  {
    activatedSortingClassName = undefined,
    className = undefined,
    projects,
    filters,
    onFilter,
    catalogId = '',
    userId = '',
    withSorting = true,
    searchHTTPHeaders = undefined,
    defaultThemeFilters = empty,
  }: ProjectFiltersBarProps,
  ref: Ref<ProjectFiltersBarImperativeRef>,
) {
  const firstRender = useRef<boolean>(true)
  const initialProjects = useRef<ProjectType[]>(projects)
  const [panelVisible, setIsPanelVisible] = useState<boolean>(false)
  const [searching, setSearching] = useState<boolean>(false)
  const [activeSubCategory, setActiveSubCategory] = useState<string | null>(
    null,
  )
  const [showSpinner, setShowSpinner] = useState<boolean>(false)
  const [searchInput, setSearchInput] = useState<string>('')
  const router = useRouter()
  const locale = router.locale as Locales
  const { t } = useTranslation('filters.component')

  const { track } = useMixpanel()

  const { trigger: triggerSearch } = useMutation<ProjectType[], any>({
    url: '/api/web/projects/search',
    headers: searchHTTPHeaders,
  })

  const ecologyLabel = getImpactSector('ecology')?.label[locale]
  const socialLabel = getImpactSector('social')?.label[locale]
  const ecologyThemes = extractThemesByImpactSector(
    filters.themes,
    'ecology',
    locale,
  )
  const socialThemes = extractThemesByImpactSector(
    filters.themes,
    'social',
    locale,
  )
  const impactState: { ecology: Theme[]; social: Theme[] } = {
    ecology: ecologyThemes.map(theme => theme.theme),
    social: socialThemes.map(theme => theme.theme),
  }

  function reducer(
    state: State,
    { type, category, tag, state: newState }: Action,
  ) {
    const isValid = !!category && !!tag
    switch (type) {
      case 'ALL': {
        setActiveSubCategory(null)
        track(`Filter projects click`, { category: 'all' })

        return {
          ...state,
          ecology: [],
          social: [],
        }
      }
      case 'ALL_ECOLOGY': {
        setActiveSubCategory('ecology')
        track(`Filter projects click`, { category: 'all_ecology' })

        return {
          ...state,
          ecology: ecologyThemes.map(theme => theme.theme),
          social: [],
        }
      }
      case 'ALL_SOCIAL': {
        setActiveSubCategory('social')
        track(`Filter projects click`, { category: 'all_social' })

        return {
          ...state,
          social: socialThemes.map(theme => theme.theme),
          ecology: [],
        }
      }
      case 'ONLY_ONE_TAG': {
        if (!category) return state

        setActiveSubCategory(category)

        return {
          ...state,
          [category]: [tag],
        }
      }
      case 'TOGGLE_IMPACT_SECTOR': {
        if (!isValid || (category !== 'social' && category !== 'ecology')) {
          return state
        }
        track(`Filter projects click`, { category })

        let list = [...state[category], tag]
        const hasAlreadyTag = state[category].some(value => tag === value)
        if (hasAlreadyTag) {
          list = state[category].filter(value => tag !== value)
          if (list.length === 0) {
            list = impactState[category]
          }
        }

        return {
          ...state,
          [category]: list,
        }
      }
      case 'TOGGLE_FILTER_TAG': {
        if (!isValid || category === 'sorting') return state

        return {
          ...state,
          [category]: toggle({ list: state[category], item: tag }),
        }
      }
      case 'SORTING_TAG': {
        if (!isValid) return state

        return {
          ...state,
          sorting: tag as SortingFilter,
        }
      }
      case 'RESET': {
        return { ...defaultState }
      }
      case 'REPLACE_STATE': {
        return { ...state, ...newState }
      }
      default:
        return state
    }
  }
  const [state, dispatch] = useReducer(reducer, defaultState)
  const ecologyCount = state?.ecology?.length || 0
  const socialCount = state?.social?.length || 0
  const isEcologyThemeAllSelected = ecologyCount === ecologyThemes.length

  const isSocialThemeAllSelected = socialCount === socialThemes.length
  const isAllSocialTagSelected =
    socialCount === socialThemes.length && ecologyCount === 0
  const isAllEcologyTagSelected =
    ecologyCount === ecologyThemes.length && socialCount === 0

  const togglePanelVisibility = () => {
    track(`Toggle filter panel visibility`, {
      panelVisible,
      search: searchInput,
      themes: [...state.ecology, ...state.social],
      region_ids: state.region,
      sorting: state.sorting,
    })
    setIsPanelVisible(prev => !prev)
  }
  const removeAllFilters = () => {
    const url = new URL(window.location.href)

    ;['region_ids', 'sorting', 'theme'].forEach(param =>
      url.searchParams.delete(param),
    )

    router.push(url, undefined, {
      shallow: true,
    })
    track(`Clear all projects filters`)
    dispatch({ type: 'RESET' })
    setActiveSubCategory(null)
    onFilter(initialProjects.current)
    setSearchInput('')
  }

  useImperativeHandle(ref, () => ({
    clearFilters() {
      removeAllFilters()
    },
  }))

  // TODO: Refactor me into something cleaner.
  // So far, we simply select the relevant filters.
  useEffect(() => {
    if (defaultThemeFilters.length === 0) return
    if (!firstRender.current) return

    const themesParams = defaultThemeFilters
    const sortedParams: State = { ...defaultState }
    // set the themes from the defaultThemeFilters
    themesParams.forEach(value => {
      const foundTheme = filters.themes.find(theme => theme.theme === value)
      const theme = foundTheme ? getTheme(foundTheme, locale) : undefined
      if (theme) {
        if (!sortedParams[theme.impact_sector]) {
          sortedParams[theme.impact_sector] = []
        }
        if (!sortedParams[theme.impact_sector].includes(theme.theme)) {
          sortedParams[theme.impact_sector].push(theme.theme)
        }
      }
    })

    let regionsParams: State['region'] = []
    if (sortedParams.region) {
      regionsParams = sortedParams.region
    }

    if (sortedParams.ecology.length !== 0) {
      setActiveSubCategory('ecology')
    } else if (sortedParams.social.length !== 0) {
      setActiveSubCategory('social')
    }

    dispatch({
      type: 'REPLACE_STATE',
      state: {
        ...sortedParams,
        region: regionsParams,
      },
    })
  }, [setActiveSubCategory, defaultThemeFilters, filters.themes, locale])

  useEffect(() => {
    if (!window.location.search) return

    const {
      themes: parsedThemesParams,
      search: parsedSearchParams,
      region_ids: parsedRegionsParams,
      sorting: parsedSortingParams,
    } = qs.parse(window.location.search || '', {
      comma: true,
      ignoreQueryPrefix: true,
    })

    const themesParams = Array.isArray(parsedThemesParams)
      ? (parsedThemesParams as string[])
      : [parsedThemesParams as string]

    const sortedParams: State = { ...defaultState }

    themesParams.forEach(value => {
      const foundTheme = filters.themes.find(theme => theme.theme === value)
      const theme = foundTheme ? getTheme(foundTheme, locale) : undefined
      if (theme) {
        if (!sortedParams[theme.impact_sector]) {
          sortedParams[theme.impact_sector] = []
        }
        if (!sortedParams[theme.impact_sector].includes(theme.theme)) {
          sortedParams[theme.impact_sector].push(theme.theme)
        }
      }
    })

    let regionsParams: State['region'] = []
    if (parsedRegionsParams) {
      regionsParams = Array.isArray(parsedRegionsParams)
        ? (parsedRegionsParams as string[]).map(regionId =>
            parseInt(regionId, 10).toString(),
          )
        : [parseInt(parsedRegionsParams as string, 10).toString()]
    }

    if (sortedParams.ecology.length !== 0) {
      setActiveSubCategory('ecology')
    } else if (sortedParams.social.length !== 0) {
      setActiveSubCategory('social')
    }

    dispatch({
      type: 'REPLACE_STATE',
      state: {
        ...sortedParams,
        region: regionsParams,
        sorting: parsedSortingParams as SortingFilter,
      },
    })

    setSearchInput((parsedSearchParams as string) || '')
  }, [filters.themes, locale])

  useEffect(
    () => {
      if (firstRender.current) {
        firstRender.current = false

        return
      }

      const allThemes = activeSubCategory
        ? [...state.ecology, ...state.social]
        : []

      const sortingTag =
        state.sorting === 'no_sorting' ? undefined : state.sorting

      const currentParams = qs.parse(window.location.search, {
        comma: true,
        ignoreQueryPrefix: true,
      })

      const params = qs.stringify(
        {
          ...currentParams,
          search: searchInput || undefined,
          themes: allThemes.length > 0 ? allThemes : undefined,
          region_ids: state.region.length > 0 ? state.region : undefined,
          sorting: sortingTag,
        },
        {
          encode: false,
          arrayFormat: 'comma',
          addQueryPrefix: true,
        },
      )

      const path = router.asPath.split('?')[0]

      router.push(`${path}${params}`, undefined, {
        shallow: true,
      })

      async function search() {
        setSearching(true)

        const timeout = setTimeout(() => {
          setShowSpinner(true)
        }, 500)

        try {
          const result =
            (await triggerSearch({
              search: searchInput || undefined,
              themes: allThemes,
              region_ids: state.region,
              sorting: state.sorting,
              locale,
              catalogId,
            })) || []

          clearTimeout(timeout)
          setShowSpinner(false)
          setSearching(false)
          track(`Filter projects`, {
            search: searchInput,
            themes: allThemes,
            region_ids: state.region,
            sorting: state.sorting,
            result: result.length,
            catalogId,
            userId,
          })
          // TODO: Axel due to some difference between backend sorting and contributors_count value we need to sort it here
          // This should be removed when the backend will be fixed
          if (state.sorting === 'by_contributor_desc') {
            onFilter(
              result.sort((a, b) =>
                a.contributors_count < b.contributors_count ? 1 : -1,
              ),
            )
          } else {
            onFilter(result)
          }
        } catch (error: unknown) {
          clearTimeout(timeout)
          setShowSpinner(false)
          setSearching(false)
        }
      }

      search()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchInput, state, triggerSearch],
  )

  const hasFilter = state?.region.length !== 0
  const hasSorting = state.sorting !== 'no_sorting'

  return (
    <>
      <div
        className={tm(
          'flex flex-col-reverse items-center justify-start gap-4 md:flex-row',
          className,
          hasSorting && activatedSortingClassName,
        )}
      >
        <div className="flex w-full flex-wrap gap-4">
          <Badge
            onClick={() => {
              dispatch({ type: 'ALL' })
            }}
            selected={activeSubCategory === null}
            disabled={searching}
            variant="gray"
            size="medium"
          >
            {t('all')}
          </Badge>
          <Badge
            onClick={() => {
              dispatch({ type: 'ALL_ECOLOGY' })
            }}
            className={tm(
              'md:block',
              defaultThemeFilters.length > 0 && 'hidden',
            )}
            selected={activeSubCategory === 'ecology'}
            disabled={searching}
            variant="gray"
            size="medium"
          >
            {ecologyLabel}
          </Badge>
          <Badge
            onClick={() => {
              dispatch({ type: 'ALL_SOCIAL' })
            }}
            className={tm(
              'md:block',
              defaultThemeFilters.length > 0 && 'hidden',
            )}
            selected={activeSubCategory === 'social'}
            disabled={searching}
            variant="gray"
            size="medium"
          >
            {socialLabel}
          </Badge>
          {defaultThemeFilters.map(theme => {
            const impactSector = filters.themes.find(
              th => th.theme === theme,
            )?.impact_sector
            const isAllSelected =
              impactSector === 'ecology'
                ? isEcologyThemeAllSelected
                : isSocialThemeAllSelected

            return (
              <Badge<Theme>
                key={theme}
                className="justify-center md:hidden"
                onClick={() => {
                  dispatch({
                    type: 'ONLY_ONE_TAG',
                    tag: theme,
                    category: impactSector,
                  })
                }}
                selected={
                  state[impactSector as 'ecology' | 'social'].includes(theme) &&
                  !isAllSelected
                }
                value={theme}
                disabled={searching}
                variant="gray"
                size="medium"
              >
                {getThemeLabel(theme, locale)}
              </Badge>
            )
          })}
        </div>
        <div className="flex w-full flex-row-reverse items-center gap-4 md:w-[unset] md:flex-row md:justify-end">
          {withSorting ? (
            <SortingMenu
              dispatch={dispatch}
              currentSortingTag={state.sorting}
            />
          ) : null}
          <Button
            className={tm(
              'h-10 max-h-10 w-10 border-inside px-4 py-2 text-sm font-semibold hover:!bg-light-gray md:min-w-[40px]  lg:w-fit lg:min-w-[unset] lg:pl-9',
              hasFilter && '!border-[1.5px] border-black focus:bg-transparent',
              !panelVisible &&
                !hasFilter &&
                ' [&:focus:not(:hover)]:bg-transparent',
            )}
            variant="secondary"
            onClick={togglePanelVisibility}
            iconLeft="position"
            iconClassName="min-w-[unset] w-4 lg:w-[14px] lg:h-[14px] min-h-[unset] h-4 left-1/2 lg:left-3 -translate-x-1/2 lg:translate-x-0"
          >
            {hasFilter ? (
              <span className="absolute -right-[6px] -top-[6px] h-[18px] w-[18px] rounded-full border-[1.5px] border-white bg-black text-[10px] leading-4 text-white">
                {state?.region.length}
              </span>
            ) : null}
            <span className="hidden whitespace-nowrap lg:block">
              {t('regions')}
            </span>
          </Button>
          <SearchBar
            showSpinner={showSpinner}
            setSearchInput={setSearchInput}
            searchInput={searchInput}
          />
        </div>
      </div>
      <div>
        {/* Themes filters on desktop  */}
        <div className="hidden md:block">
          {activeSubCategory === 'ecology' ? (
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <Badge
                onClick={() => {
                  dispatch({ type: 'ALL_ECOLOGY' })
                }}
                selected={isEcologyThemeAllSelected}
                disabled={searching}
                variant="gray"
                size="medium"
              >
                {t('all')}
              </Badge>
              {ecologyThemes.map(theme => (
                <Badge<Theme>
                  key={theme.theme}
                  className="justify-center"
                  onClick={() => {
                    dispatch({
                      type: isAllEcologyTagSelected
                        ? 'ONLY_ONE_TAG'
                        : 'TOGGLE_IMPACT_SECTOR',
                      category: 'ecology',
                      tag: theme.theme,
                    })
                  }}
                  selected={
                    state.ecology.includes(theme.theme) &&
                    !isEcologyThemeAllSelected
                  }
                  value={theme.theme}
                  disabled={searching}
                  variant="gray"
                  size="medium"
                >
                  {theme.label}
                </Badge>
              ))}
            </div>
          ) : null}
          {activeSubCategory === 'social' ? (
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <Badge
                onClick={() => {
                  dispatch({ type: 'ALL_SOCIAL' })
                }}
                selected={isSocialThemeAllSelected}
                disabled={searching}
                variant="gray"
                size="medium"
              >
                {t('all')}
              </Badge>
              {socialThemes.map(theme => (
                <Badge<Theme>
                  key={theme.theme}
                  className="justify-center"
                  onClick={() => {
                    dispatch({
                      type: isAllSocialTagSelected
                        ? 'ONLY_ONE_TAG'
                        : 'TOGGLE_IMPACT_SECTOR',
                      category: 'social',
                      tag: theme.theme,
                    })
                  }}
                  selected={
                    state.social.includes(theme.theme) &&
                    !isSocialThemeAllSelected
                  }
                  value={theme.theme}
                  disabled={searching}
                  variant="gray"
                  size="medium"
                >
                  {theme.label}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
        {/* --  */}
      </div>
      <FilterPanel
        togglePanelVisibility={togglePanelVisibility}
        panelVisible={panelVisible}
        removeAllFilters={removeAllFilters}
        filters={filters}
        dispatch={dispatch}
        state={state}
        projects={projects}
        searching={searching}
      />
    </>
  )
}

export const ProjectFiltersBar = forwardRef(ProjectFiltersBarWithRef)
